import React from "react";
import { useToast, VStack, FormControl, FormErrorMessage, HStack, Checkbox, CheckboxGroup, Stack, RadioGroup, Radio, Text, Button } from "@chakra-ui/react";
import { StepperModal } from "../../../../_components/Modals";
import { ThemedFormLabel } from "../../../homeowners/hire/themed-form-label";
import { ThemedInput } from "../../../homeowners/hire/themed-input";
import { organizationInfoSchema, contactInfoSchema, maintenanceDetailsSchema, OrganizationInfoFormData, MaintenanceContactInfoFormData, MaintenanceDetailsFormData } from "./schema";
import { FacilityService, MaintenancePlanType, MaintenanceFrequency } from "../../../../__generated__/graphql";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { gql, useMutation } from "@apollo/client";
import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import { normalizeString } from "lib/utils";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  userType: "business" | "homeowner";
}

const REQUEST_FACILITY_MAINTENANCE = gql`
  mutation requestFacilityMaintenance(
    $facilityMaintenanceRequestDto: FacilityMaintenanceRequestDto!
  ) {
    requestFacilityMaintenance(
      facilityMaintenanceRequestDto: $facilityMaintenanceRequestDto
    )
  }
`;

export const FacilityMaintenanceOutsourcingModal: React.FC<Props> = ({
  isOpen,
  onClose,
  userType,
}) => {
  const toast = useToast();
  const [currentStep, setCurrentStep] = React.useState(1);
  const totalSteps = userType === "business" ? 4 : 3;

  const [requestFacilityMaintenance, { loading }] = useMutation(
    REQUEST_FACILITY_MAINTENANCE
  );

  const orgForm = useForm<OrganizationInfoFormData>({
    resolver: zodResolver(organizationInfoSchema),
    defaultValues: { organization: "", roleInOrganization: "", industry: "" },
  });

  const contactForm = useForm<MaintenanceContactInfoFormData>({
    resolver: zodResolver(contactInfoSchema),
    defaultValues: { firstName: "", lastName: "", email: "", phone: "" },
  });

  const detailsForm = useForm<MaintenanceDetailsFormData>({
    resolver: zodResolver(maintenanceDetailsSchema),
    defaultValues: {
      facilityLocation: "",
      services: [],
      otherService: "",
      planType: undefined,
      maintenanceFrequency: undefined,
    },
  });

  const resetAll = () => {
    setCurrentStep(1);
    orgForm.reset();
    contactForm.reset();
    detailsForm.reset();
  };

  const handleSubmitAll = async () => {
    const organizationInfo =
      userType === "business" ? orgForm.getValues() : undefined;
    const contactInfo = contactForm.getValues();
    const details = detailsForm.getValues();

    try {
      await requestFacilityMaintenance({
        variables: {
          facilityMaintenanceRequestDto: {
            organizationInfo,
            contactInfo,
            facilityLocation: details.facilityLocation,
            services: details.services,
            otherService: details.otherService || undefined,
            planType: details.planType,
            maintenanceFrequency:
              details.planType === "ROUTINE"
                ? details.maintenanceFrequency
                : undefined,
          },
        },
      });

      toast({
        title: "Request Submitted Successfully",
        description:
          "Thank you for your request. Our customer success team will contact you shortly.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      resetAll();
      onClose();
    } catch (error) {
      toast({
        title: "Submission Failed",
        description:
          "Please try again or contact support if the issue persists.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const renderStep = () => {
    const isBusiness = userType === "business";
    if (isBusiness && currentStep === 1) {
      const {
        register,
        handleSubmit,
        formState: { errors },
      } = orgForm;
      return (
        <form onSubmit={handleSubmit(() => setCurrentStep(2))}>
          <VStack spacing={6} align="stretch">
            <FormControl isInvalid={!!errors.organization}>
              <ThemedFormLabel>Organization Name</ThemedFormLabel>
              <ThemedInput
                {...register("organization")}
                placeholder="Enter your organization name"
              />
              <FormErrorMessage>
                {errors.organization?.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!errors.roleInOrganization}>
              <ThemedFormLabel>Your role in the organization</ThemedFormLabel>
              <ThemedInput
                {...register("roleInOrganization")}
                placeholder="e.g., Facility Manager"
              />
              <FormErrorMessage>
                {errors.roleInOrganization?.message}
              </FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!errors.industry}>
              <ThemedFormLabel>Industry (optional)</ThemedFormLabel>
              <ThemedInput
                {...register("industry")}
                placeholder="Enter your industry"
              />
              <FormErrorMessage>{errors.industry?.message}</FormErrorMessage>
            </FormControl>
            <Button
              type="submit"
              size="lg"
              w="full"
              colorScheme="flatteredFlamingo"
              rightIcon={<ArrowForwardIcon />}
              isLoading={loading}
              loadingText="Processing..."
              mt={4}
            >
              Next
            </Button>
          </VStack>
        </form>
      );
    }

    const {
      register: regC,
      handleSubmit: subC,
      formState: { errors: eC },
    } = contactForm;
    if (
      (userType === "homeowner" && currentStep === 1) ||
      (userType === "business" && currentStep === 2)
    ) {
      return (
        <form onSubmit={subC(() => setCurrentStep(currentStep + 1))}>
          <VStack spacing={6} align="stretch">
            <HStack spacing={4}>
              <FormControl isInvalid={!!eC.firstName}>
                <ThemedFormLabel>First Name</ThemedFormLabel>
                <ThemedInput
                  {...regC("firstName")}
                  placeholder="Enter your first name"
                />
                <FormErrorMessage>{eC.firstName?.message}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!eC.lastName}>
                <ThemedFormLabel>Last Name</ThemedFormLabel>
                <ThemedInput
                  {...regC("lastName")}
                  placeholder="Enter your last name"
                />
                <FormErrorMessage>{eC.lastName?.message}</FormErrorMessage>
              </FormControl>
            </HStack>
            <FormControl isInvalid={!!eC.email}>
              <ThemedFormLabel>Email Address</ThemedFormLabel>
              <ThemedInput
                type="email"
                {...regC("email")}
                placeholder="Enter your email"
              />
              <FormErrorMessage>{eC.email?.message}</FormErrorMessage>
            </FormControl>
            <FormControl isInvalid={!!eC.phone}>
              <ThemedFormLabel>Phone Number</ThemedFormLabel>
              <ThemedInput
                type="tel"
                {...regC("phone")}
                placeholder="Enter your phone number"
              />
              <FormErrorMessage>{eC.phone?.message}</FormErrorMessage>
            </FormControl>
            <HStack spacing={4} mt={4}>
              {currentStep > 1 && userType === "business" && (
                <Button
                  type="button"
                  onClick={() => setCurrentStep(currentStep - 1)}
                  leftIcon={<ArrowBackIcon />}
                  size="lg"
                  flex="1"
                  variant="outline"
                  isDisabled={loading}
                >
                  Previous
                </Button>
              )}
              <Button
                type="submit"
                size="lg"
                flex="1"
                colorScheme="flatteredFlamingo"
                rightIcon={<ArrowForwardIcon />}
                isLoading={loading}
                loadingText="Processing..."
              >
                Next
              </Button>
            </HStack>
          </VStack>
        </form>
      );
    }

    const {
      register,
      handleSubmit,
      watch,
      setValue,
      formState: { errors },
    } = detailsForm;
    const planType = watch("planType");
    const services = watch("services") as unknown as FacilityService[];

    const toggleServices = (value: string[]) =>
      setValue("services", value as FacilityService[], {
        shouldValidate: true,
      });

    return (
      <form onSubmit={handleSubmit(() => handleSubmitAll())}>
        <VStack spacing={6} align="stretch">
          <FormControl isInvalid={!!errors.facilityLocation}>
            <ThemedFormLabel>Facility Location</ThemedFormLabel>
            <ThemedInput
              {...register("facilityLocation")}
              placeholder="Enter facility location"
            />
            <FormErrorMessage>
              {errors.facilityLocation?.message}
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.services}>
            <ThemedFormLabel>Type of Service Required</ThemedFormLabel>
            <CheckboxGroup
              value={services as any}
              onChange={(v) => toggleServices(v as string[])}
            >
              <Stack direction="column">
                {Object.values(FacilityService)
                  .filter((service) => service !== FacilityService.Other)
                  .map((service) => (
                    <Checkbox key={service} value={service}>
                      {normalizeString(service)}
                    </Checkbox>
                  ))}
                <Checkbox value={FacilityService.Other}>
                  {normalizeString(FacilityService.Other)}
                </Checkbox>
              </Stack>
            </CheckboxGroup>
            <FormErrorMessage>
              {(errors as any).services?.message}
            </FormErrorMessage>
          </FormControl>

          {services?.includes(FacilityService.Other) && (
            <FormControl isInvalid={!!errors.otherService}>
              <ThemedFormLabel>Other service (please specify)</ThemedFormLabel>
              <ThemedInput
                {...register("otherService")}
                placeholder="e.g., Pest control"
              />
              <FormErrorMessage>
                {errors.otherService?.message}
              </FormErrorMessage>
            </FormControl>
          )}

          <FormControl isInvalid={!!errors.planType}>
            <ThemedFormLabel>
              Is this a one-off request or routine maintenance?
            </ThemedFormLabel>
            <RadioGroup
              value={planType}
              onChange={(v) =>
                setValue("planType", v as MaintenancePlanType, {
                  shouldValidate: true,
                })
              }
            >
              <HStack spacing={6}>
                <Radio value={MaintenancePlanType.OneOff}>
                  One-off request
                </Radio>
                <Radio value={MaintenancePlanType.Routine}>
                  Routine maintenance
                </Radio>
              </HStack>
            </RadioGroup>
            <FormErrorMessage>
              {(errors as any).planType?.message}
            </FormErrorMessage>
          </FormControl>

          {planType === "ROUTINE" && (
            <FormControl isInvalid={!!errors.maintenanceFrequency}>
              <ThemedFormLabel>
                What is the frequency of maintenance?
              </ThemedFormLabel>
              <select
                {...register("maintenanceFrequency")}
                className="min-w-full chakra-input css-0"
                style={{ padding: "10px", border: "1px solid #E2E8F0" }}
              >
                {Object.values(MaintenanceFrequency).map((frequency) => (
                  <option key={frequency} value={frequency}>
                    {normalizeString(frequency)}
                  </option>
                ))}
              </select>
              <FormErrorMessage>
                {(errors as any).maintenanceFrequency?.message}
              </FormErrorMessage>
            </FormControl>
          )}

          <HStack spacing={4} mt={4}>
            <Button
              type="button"
              onClick={() => setCurrentStep(currentStep - 1)}
              leftIcon={<ArrowBackIcon />}
              size="lg"
              flex="1"
              variant="outline"
              isDisabled={loading}
            >
              Previous
            </Button>
            <Button
              type="submit"
              size="lg"
              flex="1"
              colorScheme="flatteredFlamingo"
              rightIcon={<ArrowForwardIcon />}
              isLoading={loading}
              loadingText="Submitting..."
            >
              Submit Request
            </Button>
          </HStack>
        </VStack>
      </form>
    );
  };

  const titleForStep = () => {
    if (userType === "business") {
      switch (currentStep) {
        case 1:
          return "Tell us about your organization";
        case 2:
          return "Tell us about yourself";
        case 3:
          return "Facility maintenance details";
        default:
          return "Facility Maintenance";
      }
    }
    switch (currentStep) {
      case 1:
        return "Tell us about yourself";
      case 2:
        return "Facility maintenance details";
      default:
        return "Facility Maintenance";
    }
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const onNext = async () => {
    if (userType === "business") {
      if (currentStep === 1) {
        if (await orgForm.trigger()) setCurrentStep(2);
        return;
      }
      if (currentStep === 2) {
        if (await contactForm.trigger()) setCurrentStep(3);
        return;
      }
      if (currentStep === 3) {
        if (await detailsForm.trigger()) await handleSubmitAll();
        return;
      }
    } else {
      if (currentStep === 1) {
        if (await contactForm.trigger()) setCurrentStep(2);
        return;
      }
      if (currentStep === 2) {
        if (await detailsForm.trigger()) await handleSubmitAll();
        return;
      }
    }
  };

  const onPrev = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  return (
    <StepperModal
      isOpen={isOpen}
      onClose={handleClose}
      title={titleForStep()}
      onSubmit={onNext}
      currentStep={currentStep}
      totalSteps={totalSteps}
      isLoading={loading}
      onReset={resetAll}
      onPrevious={onPrev}
    >
      {renderStep()}
    </StepperModal>
  );
};


